import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  PENDING_WRITER_PROMPT_KEY,
  takePendingIntent,
} from '@/lib/auth/pendingIntent';

export type WriterMessagePart = { type: 'text'; text: string };

export interface WriterCiteChange {
  anchor_id: string;
  citekey: string;
}

export interface WriterTurnDiff {
  bib_added: string[];
  bib_removed: string[];
  /** `\cite{}` occurrences that actually landed in a section body. */
  cites_added?: WriterCiteChange[];
  cites_removed?: WriterCiteChange[];
  sections_created: Array<{ anchor_id: string; label: string; title: string | null }>;
  sections_removed: Array<{ anchor_id: string; label: string }>;
  sections_patched: Array<{ anchor_id: string; label: string }>;
  preamble_changed: boolean;
  is_empty: boolean;
}

/**
 * Readable sentence-level diff (mirror of the edge `TurnTextDiff` shape in
 * `supabase/functions/writer-agent/turnTextDiff.ts`). Presentation only —
 * never fed back to the model.
 */
export type WriterTextDiffOp = 'equal' | 'insert' | 'delete';

export type WriterTextDiffChunk =
  | { op: WriterTextDiffOp; text: string }
  | { op: 'gap'; count: number };

export interface WriterTextDiffSection {
  anchor_id: string;
  title: string;
  kind: 'section' | 'bib';
  change: 'created' | 'edited' | 'removed';
  magnitude: string;
  weight: 1 | 2 | 3 | 4;
  chunks: WriterTextDiffChunk[];
  raw: Array<{ op: WriterTextDiffOp; text: string }>;
  cites_added: string[];
  truncated: boolean;
}

export interface WriterTextDiff {
  sections: WriterTextDiffSection[];
  truncated: boolean;
  version: 1;
}

/** Guards against payloads from an older/newer server schema. */
export function asWriterTextDiff(value: unknown): WriterTextDiff | undefined {
  const v = value as WriterTextDiff | undefined;
  if (!v || v.version !== 1 || !Array.isArray(v.sections)) return undefined;
  return v.sections.length ? v : undefined;
}

export interface WriterMemoryNoteRef {
  id: string;
  kind: string;
  note: string;
}

export interface WriterToolActivityEvent {
  name: string;
  ok: boolean;
  auto?: boolean;
  phase?: number;
  error?: string;
  /** Memory notes written by this call, shown as a quiet "Noted:" line. */
  notes?: WriterMemoryNoteRef[];
}

export interface WriterToolActivity {
  name: string;
  status: 'running' | 'done';
  ok?: boolean;
  auto?: boolean;
  error?: string;
  args?: Record<string, unknown>;
  notes?: WriterMemoryNoteRef[];
}

export interface WriterMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  parts: WriterMessagePart[];
  streaming?: boolean;
  created_at?: string;
  toolActivity?: WriterToolActivity[];
  /** True while the AI service has been silent for a while on the current step. */
  aiSlow?: boolean;
  // Verified structured diff produced by the Phase 2 pipeline. Present
  // on assistant messages once the turn's ground-truth diff is emitted.
  diff?: WriterTurnDiff;
  /** Readable sentence-level diff rendered by `WriterTurnDiffCard`. */
  textDiff?: WriterTextDiff;
  toolEvents?: WriterToolActivityEvent[];
  unresolvedCites?: string[];
}


export interface UseWriterThreadResult {
  threadId: string | null;
  messages: WriterMessage[];
  streaming: boolean;
  error: string | null;
  outOfCredits: boolean;
  loading: boolean;
  send: (text: string) => Promise<void>;
}

const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/writer-agent`;

async function writerRequestError(response: Response): Promise<string> {
  let detail = '';
  try {
    const body = (await response.json()) as { message?: unknown; error?: unknown };
    if (typeof body.message === 'string') detail = body.message;
    else if (typeof body.error === 'string') detail = body.error;
  } catch {
    // Some gateways return an empty or non-JSON error body.
  }

  if (detail === 'email_not_confirmed') {
    return 'Please confirm your email address before using PaperVerse Writer.';
  }
  if (detail === 'ai_objection_active') {
    return 'AI processing is disabled for your account. You can change this in your privacy settings.';
  }
  if (detail === 'writer_disabled') {
    return 'PaperVerse Writer is temporarily unavailable.';
  }
  if (detail === 'Forbidden') {
    return 'Writer could not verify access to this draft. Please reload and try again.';
  }
  return detail || `The assistant could not respond (error ${response.status}). Please try again.`;
}

/** Memory notes written during a turn, flattened for the chat's "Noted" line. */
export function memoryNotesFrom(
  activities: WriterToolActivity[] | undefined,
): WriterMemoryNoteRef[] {
  if (!activities?.length) return [];
  const seen = new Set<string>();
  const out: WriterMemoryNoteRef[] = [];
  for (const activity of activities) {
    for (const note of activity.notes ?? []) {
      if (!note?.note || seen.has(note.id)) continue;
      seen.add(note.id);
      out.push(note);
    }
  }
  return out;
}

function activityEventsToCards(
  events: WriterToolActivityEvent[] | undefined,
): WriterToolActivity[] | undefined {
  if (!events?.length) return undefined;
  return events.map((event) => ({
    name: event.name,
    status: 'done' as const,
    ok: event.ok,
    auto: event.auto,
    error: event.error,
    notes: event.notes,
  }));
}


/** Maps a persisted `writer_messages` row into the in-memory shape. */
export function rowToWriterMessage(m: any): WriterMessage {
  const rawParts = Array.isArray(m.parts) ? m.parts : [];
  const textParts: WriterMessagePart[] = rawParts
    .filter((p: any) => p?.type === 'text' && typeof p.text === 'string')
    .map((p: any) => ({ type: 'text' as const, text: p.text }));
  const diffPart = rawParts.find((p: any) => p?.type === 'diff');
  const toolActPart = rawParts.find((p: any) => p?.type === 'tool_activity');
  const uncitesPart = rawParts.find((p: any) => p?.type === 'unresolved_cites');
  const textDiffPart = rawParts.find((p: any) => p?.type === 'text_diff');
  const events = Array.isArray(toolActPart?.events) ? toolActPart.events : undefined;
  return {
    id: m.id,
    role: m.role,
    parts: textParts.length ? textParts : [{ type: 'text', text: '' }],
    created_at: m.created_at,
    diff: diffPart?.diff,
    textDiff: asWriterTextDiff(textDiffPart?.text_diff),
    toolEvents: events,
    toolActivity: activityEventsToCards(events),
    unresolvedCites: Array.isArray(uncitesPart?.citekeys) ? uncitesPart.citekeys : undefined,
  };
}

export const WRITER_STILL_WORKING_MESSAGE =
  'The live connection dropped, but Writer is still working on your request. The reply will appear here when it is done.';
export const WRITER_INTERRUPTED_MESSAGE =
  'That request was interrupted before Writer could reply. Anything it already changed is saved in your draft. Ask again to continue.';
export const WRITER_AI_UNAVAILABLE_MESSAGE =
  'Writer could not get an answer from the AI service. Nothing was charged. Send "continue" to try again.';

/** Turn a raw server error into a sentence an author can act on. */
export function writerErrorText(raw: unknown): string {
  const text = typeof raw === 'string' ? raw : '';
  if (!text) return 'Something went wrong. Nothing was charged. Please try again.';
  if (/^Vertex \d+/.test(text) || /ai service/i.test(text)) return WRITER_AI_UNAVAILABLE_MESSAGE;
  return text;
}


/**
 * A turn can outlive the live stream by several minutes. Poll for the
 * persisted reply and append it when it lands. Resolves false after the
 * window, which means the turn was cut off without a reply.
 */
export async function waitForLateTurn(
  threadId: string | null,
  sinceIso: string,
  setMessages: (updater: (prev: WriterMessage[]) => WriterMessage[]) => void,
  attempts = 60,
  delayMs = 6000,
): Promise<boolean> {
  if (!threadId) return false;
  for (let i = 0; i < attempts; i += 1) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    const { data } = await supabase
      .from('writer_messages')
      .select('id, role, parts, created_at')
      .eq('thread_id', threadId)
      .eq('role', 'assistant')
      .gt('created_at', sinceIso)
      .order('created_at', { ascending: false })
      .limit(1);
    const row = data?.[0];
    if (row) {
      const adopted = rowToWriterMessage(row);
      setMessages((prev) =>
        prev.some((m) => m.id === adopted.id) ? prev : [...prev, { ...adopted, streaming: false }],
      );
      return true;
    }
  }
  return false;
}

/**
 * The stream ended without a `done` event. The agent keeps running
 * server-side and persists its reply, so poll briefly for the assistant
 * message written after this turn started and adopt it in place of the
 * stranded local bubble.
 */
export async function adoptPersistedTurn(
  threadId: string | null,
  sinceIso: string,
  assistantMsgId: string,
  setMessages: (updater: (prev: WriterMessage[]) => WriterMessage[]) => void,
  attempts = 6,
  delayMs = 2000,
): Promise<boolean> {
  if (!threadId) return false;
  for (let i = 0; i < attempts; i += 1) {
    const { data } = await supabase
      .from('writer_messages')
      .select('id, role, parts, created_at')
      .eq('thread_id', threadId)
      .eq('role', 'assistant')
      .gt('created_at', sinceIso)
      .order('created_at', { ascending: false })
      .limit(1);
    const row = data?.[0];
    if (row) {
      const adopted = rowToWriterMessage(row);
      setMessages((prev) =>
        prev.map((m) => (m.id === assistantMsgId ? { ...adopted, streaming: false } : m)),
      );
      return true;
    }
    if (i < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return false;
}


export interface WriterTurnRun {
  id: string;
  status: 'running' | 'done' | 'interrupted';
  phase: string | null;
  hop: number;
  started_at: string;
  updated_at: string;
}

/** A run that has not reported for this long was cut off by the server. */
export const WRITER_RUN_STALE_MS = 8 * 60_000;

export function isLiveRun(run: WriterTurnRun | null, now: number = Date.now()): boolean {
  if (!run || run.status !== 'running') return false;
  return now - new Date(run.updated_at).getTime() < WRITER_RUN_STALE_MS;
}

/** Placeholder bubble for a request still running on the server with no live stream. */
export function runPlaceholderMessage(run: WriterTurnRun): WriterMessage {
  return {
    id: `run-${run.id}`,
    role: 'assistant',
    parts: [{ type: 'text', text: '' }],
    streaming: true,
    toolActivity: run.phase ? [{ name: run.phase, status: 'running' }] : [],
  };
}

/**
 * Loads the most recent Writer thread for a project and exposes a
 * streaming `send` that appends to the local message list while the
 * `writer-agent` edge function pushes SSE deltas.
 *
 * Admin-only surface — bounces gracefully if the server rejects the
 * request, matching the Writer feature-flag gate.
 */
export function useWriterThread(projectId: string | undefined): UseWriterThreadResult {
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<WriterMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [outOfCredits, setOutOfCredits] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // Mirrors `streaming` for effects that must not clobber a live turn.
  const streamingRef = useRef(false);
  // Latest server-side run for this thread (survives page reloads).
  const [run, setRun] = useState<WriterTurnRun | null>(null);

  const reloadMessages = useCallback(async (tid: string) => {
    const { data: msgs } = await supabase
      .from('writer_messages')
      .select('id, role, parts, created_at')
      .eq('thread_id', tid)
      .order('created_at', { ascending: true });
    if (!streamingRef.current) setMessages((msgs ?? []).map(rowToWriterMessage));
  }, []);

  useEffect(() => {
    if (!threadId) return;
    let cancelled = false;
    const db = supabase as any;
    void db
      .from('writer_turn_runs')
      .select('id, status, phase, hop, started_at, updated_at')
      .eq('thread_id', threadId)
      .order('started_at', { ascending: false })
      .limit(1)
      .then(({ data }: { data: WriterTurnRun[] | null }) => {
        if (!cancelled) setRun(data?.[0] ?? null);
      });
    const channel = supabase
      .channel(`writer-runs-${threadId}`)
      .on(
        'postgres_changes' as never,
        { event: '*', schema: 'public', table: 'writer_turn_runs', filter: `thread_id=eq.${threadId}` },
        (payload: { new?: WriterTurnRun }) => {
          const next = payload.new;
          if (!next?.id) return;
          setRun((prev) => {
            if (prev && prev.id !== next.id && new Date(prev.started_at) > new Date(next.started_at)) return prev;
            return next;
          });
          // A continuation hop just saved a reply, or the run ended.
          void reloadMessages(threadId);
        },
      )
      .subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [threadId, reloadMessages]);

  // Re-check staleness once a minute so a cut-off run stops looking alive.
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    if (run?.status !== 'running') return;
    const t = setInterval(() => setClock(Date.now()), 60_000);
    return () => clearInterval(t);
  }, [run?.status]);

  const backgroundRun = !streaming && isLiveRun(run, clock) ? run : null;
  const runInterrupted =
    !streaming && run !== null &&
    (run.status === 'interrupted' || (run.status === 'running' && !isLiveRun(run, clock))) &&
    // Only when the request is still unanswered; a saved error reply already explains it.
    messages[messages.length - 1]?.role === 'user';

  // The run record is the server's truth. Once it says the run ended, drop
  // the "still working" banner right away instead of polling for minutes.
  useEffect(() => {
    if (run && run.status !== 'running') {
      setError((current) => (current === WRITER_STILL_WORKING_MESSAGE ? null : current));
    }
  }, [run]);



  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: threads } = await supabase
        .from('writer_threads')
        .select('id')
        .eq('project_id', projectId)
        .order('updated_at', { ascending: false })
        .limit(1);
      const tid = threads?.[0]?.id ?? null;
      if (cancelled) return;
      setThreadId(tid);
      if (tid) {
        const { data: msgs } = await supabase
          .from('writer_messages')
          .select('id, role, parts, created_at')
          .eq('thread_id', tid)
          .order('created_at', { ascending: true });
        // A remount mid-turn must not wipe the in-flight assistant bubble.
        if (!cancelled && !streamingRef.current) {
          setMessages((msgs ?? []).map(rowToWriterMessage));
        }

      } else if (!streamingRef.current) {
        setMessages([]);
      }

      setLoading(false);
    })();
    return () => {
      cancelled = true;
      // Deliberately NOT aborting an in-flight turn here. This cleanup also
      // runs on StrictMode double-mounts and on any remount (e.g. crossing
      // the desktop/compact breakpoint), and killing the request there is
      // what produced "The stream controller cannot close or enqueue".
      // The server finishes and persists the turn either way.
    };
  }, [projectId]);


  const send = useCallback(
    async (text: string) => {
      if (!projectId || !text.trim() || streaming || backgroundRun) return;
      setError(null);
      setOutOfCredits(false);
      const trimmed = text.trim();
      const userMsgId = `local-user-${Date.now()}`;
      const assistantMsgId = `local-assistant-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        { id: userMsgId, role: 'user', parts: [{ type: 'text', text: trimmed }] },
        {
          id: assistantMsgId,
          role: 'assistant',
          parts: [{ type: 'text', text: '' }],
          streaming: true,
        },
      ]);
      streamingRef.current = true;
      setStreaming(true);

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        setError('Not signed in');
        streamingRef.current = false;
        setStreaming(false);
        return;
      }

      const ac = new AbortController();
      abortRef.current = ac;
      const turnStartedIso = new Date().toISOString();
      let activeThreadId = threadId;
      let sawDone = false;
      // The server reported a failure: it has stopped, so never claim it is
      // "still working" afterwards.
      let sawError = false;
      let transportError: string | null = null;
      try {
        const res = await fetch(FUNCTIONS_URL, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,

            'Content-Type': 'application/json',
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            project_id: projectId,
            thread_id: threadId,
            message: trimmed,
          }),
          signal: ac.signal,
        });
        if (!res.ok) {
          throw new Error(await writerRequestError(res));
        }
        if (!res.body) {
          throw new Error('The assistant returned no response. Please try again.');
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let acc = '';
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() ?? '';
          for (const evt of events) {
            const lines = evt.split('\n');
            const eventLine = lines.find((l) => l.startsWith('event:'));
            const dataLine = lines.find((l) => l.startsWith('data:'));
            if (!eventLine || !dataLine) continue;
            const eventName = eventLine.slice(6).trim();
            const payload = dataLine.slice(5).trim();
            let data: any = null;
            try {
              data = JSON.parse(payload);
            } catch {
              continue;
            }
            if (eventName === 'meta' && data?.thread_id) {
              activeThreadId = data.thread_id;
              setThreadId(data.thread_id);

            } else if (eventName === 'delta' && typeof data?.text === 'string') {
              acc += data.text;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId
                    ? { ...m, parts: [{ type: 'text', text: acc }] }
                    : m,
                ),
              );
            } else if (eventName === 'error') {
              sawError = true;
              if (data?.code === 'insufficient_credits') {
                setOutOfCredits(true);
                setError('You are out of credits. Upgrade your plan to keep writing.');
              } else {
                setError(writerErrorText(data?.message));
              }
            } else if (eventName === 'ai_wait') {
              const slow = Boolean(data?.slow);
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantMsgId ? { ...m, aiSlow: slow } : m)),
              );
            } else if (eventName === 'tool_call') {
              const name = String(data?.name ?? 'tool');
              const args =
                data?.args && typeof data.args === 'object'
                  ? (data.args as Record<string, unknown>)
                  : undefined;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId
                    ? {
                        ...m,
                        toolActivity: [
                          ...(m.toolActivity ?? []),
                          { name, status: 'running', args },
                        ],
                      }
                    : m,
                ),
              );
            } else if (eventName === 'tool_result') {
              const name = String(data?.name ?? 'tool');
              const ok = Boolean(data?.ok);
              const auto = Boolean(data?.auto);
              const error = typeof data?.error === 'string' ? data.error : undefined;
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.id !== assistantMsgId) return m;
                  const acts = [...(m.toolActivity ?? [])];
                  let matched = false;
                  for (let i = acts.length - 1; i >= 0; i -= 1) {
                    if (acts[i].name === name && acts[i].status === 'running') {
                      acts[i] = { ...acts[i], status: 'done', ok, auto, error };
                      matched = true;
                      break;
                    }
                  }
                  if (!matched) acts.push({ name, status: 'done', ok, auto, error });
                  return { ...m, toolActivity: acts };
                }),
              );
            } else if (eventName === 'diff') {
              const diff = data?.diff as WriterTurnDiff | undefined;
              const cites = Array.isArray(data?.unresolved_cites)
                ? (data.unresolved_cites as string[])
                : undefined;
              // `diff` can arrive twice (Phase 1, then again after a
              // post-Phase-2 mutation). Keep the last non-empty payload
              // rather than flickering the card away.
              const textDiff = asWriterTextDiff(data?.text_diff);
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId
                    ? { ...m, diff, textDiff: textDiff ?? m.textDiff, unresolvedCites: cites }
                    : m,
                ),
              );
            } else if (eventName === 'tool_activity') {
              const events = Array.isArray(data?.events)
                ? (data.events as WriterToolActivityEvent[])
                : [];
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId
                    ? {
                        ...m,
                        toolEvents: events,
                        toolActivity: activityEventsToCards(events) ?? m.toolActivity,
                      }
                    : m,
                ),
              );
            } else if (eventName === 'done') {
              const finalText = typeof data?.text === 'string' ? data.text : acc;
              const diff = data?.diff as WriterTurnDiff | undefined;
              const cites = Array.isArray(data?.unresolved_cites)
                ? (data.unresolved_cites as string[])
                : undefined;
              const events = Array.isArray(data?.tool_activity)
                ? (data.tool_activity as WriterToolActivityEvent[])
                : undefined;

              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId
                    ? {
                        ...m,
                        id: data?.message_id ?? m.id,
                        streaming: false,
                        parts: [{ type: 'text', text: finalText }],
                        diff: diff ?? m.diff,
                        textDiff: asWriterTextDiff(data?.text_diff) ?? m.textDiff,
                        unresolvedCites: cites ?? m.unresolvedCites,
                        toolEvents: events ?? m.toolEvents,
                        toolActivity: activityEventsToCards(events) ?? m.toolActivity,
                      }
                    : m,
                ),
              );
              sawDone = true;
            }
          }
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          transportError = err instanceof Error ? err.message : String(err);
        }
      } finally {
        if (!sawDone) {
          // The connection dropped before the turn reported back (reload,
          // network blip, proxy). The server keeps working and persists the
          // result, so adopt it instead of stranding an empty bubble.
          const adopted = await adoptPersistedTurn(
            activeThreadId,
            turnStartedIso,
            assistantMsgId,
            setMessages,
          );
          if (adopted && sawError) {
            // The saved error reply explains the failure; no banner needed.
            setError(null);
          } else if (!adopted) {
            // A failed request never produced an assistant message. Remove the
            // optimistic placeholder instead of presenting it as an empty reply.
            setMessages((prev) => prev.filter((message) => message.id !== assistantMsgId));
            if (sawError) {
              // Keep the error already shown from the stream.
            } else if (transportError) {
              setError(transportError);
            } else {
              // Long turns keep running server-side for minutes. Keep watching
              // for the reply instead of asking the user to reload.
              setError(WRITER_STILL_WORKING_MESSAGE);
              void waitForLateTurn(activeThreadId, turnStartedIso, setMessages).then((found) => {
                setError((current) =>
                  current === WRITER_STILL_WORKING_MESSAGE
                    ? found
                      ? null
                      : WRITER_INTERRUPTED_MESSAGE
                    : current,
                );
              });
            }
          }
        } else if (transportError) {
          setError(transportError);
        }
        streamingRef.current = false;
        setStreaming(false);
        setMessages((prev) =>
          prev.map((m) => (m.streaming ? { ...m, streaming: false } : m)),
        );
      }

    },
    [projectId, threadId, streaming, backgroundRun],
  );

  // Replay a guest prompt captured before the auth wall (see
  // WriterGuestLanding / WriterHome). takePendingIntent clears the key
  // BEFORE dispatch, so a refresh or StrictMode remount can never send
  // the same prompt twice.
  useEffect(() => {
    if (loading || !projectId) return;
    const pending = takePendingIntent(PENDING_WRITER_PROMPT_KEY);
    if (!pending || !pending.trim()) return;
    void send(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot replay; `send` identity churn must not re-fire it
  }, [loading, projectId]);

  const visibleMessages = backgroundRun
    ? [...messages, runPlaceholderMessage(backgroundRun)]
    : messages;
  const visibleError =
    error ?? (runInterrupted && !loading ? WRITER_INTERRUPTED_MESSAGE : null);

  return {
    threadId,
    messages: visibleMessages,
    streaming: streaming || Boolean(backgroundRun),
    error: visibleError,
    outOfCredits,
    loading,
    send,
  };
}
